import { redirect } from "react-router-dom";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { APP_VERSION, STORAGE_AUTH_TOKEN } from "constants/variables";
import {
  FavoriteRequestType,
  CategoryResponseType,
  GamesResponseType,
  ParamsRequestType,
  ProviderResponseType,
  FavoritesResponseType,
  HistoryResponseType,
  InitByAliasGameRequestType,
  InitGameByAliasResponseType,
  NewHistoryResponseType,
  GameType,
} from "types/Games";
import { getLocalStorageItem } from "utils/localStorage";
import url from "url/index";
import { useLoaderStore } from "stores/loader";
import { setServiceError } from "stores/settings";
import { dynamicBaseQuery } from "utils/dynamicBaseQuery";
import {
  setGames,
  setGameUrl,
  setHistory,
  setLastPage,
  setMoreGames,
  setProviders,
  setSearchResult,
  setSearchResultMore,
  setCurrentGame,
  setNewHistory,
} from "./slice";

export const gamesApi = createApi({
  reducerPath: "gamesApi",
  baseQuery: dynamicBaseQuery,
  endpoints: (build) => ({
    categories: build.query<CategoryResponseType, string>({
      query: () => ({
        url: url.config.api.games.categories,
      }),
    }),
    games: build.query<GamesResponseType, ParamsRequestType>({
      query: (params: ParamsRequestType) => ({
        url: url.config.api.games.games,
        headers: {
          Authorization: getLocalStorageItem(STORAGE_AUTH_TOKEN)
            ? `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`
            : "",
          "f-v": APP_VERSION,
        },
        params,
      }),
      keepUnusedDataFor: 1,
      async onQueryStarted(
        request: ParamsRequestType,
        { dispatch, queryFulfilled },
      ) {
        const { setContentLoading } = useLoaderStore.getState();

        // Если скорость интернета пользователя меньше 0.7 Mbps, то показываем скелетон
        if (window.navigator && window.navigator.connection) {
          const connectionSpeed = window.navigator.connection.downlink;
          if (connectionSpeed <= 0.7) {
            setContentLoading(true);
          }
        }

        try {
          const { data } = await queryFulfilled;

          setContentLoading(false);
          // eslint-disable-next-line sonarjs/no-collapsible-if
          if (data.statusCode === 200) {
            if (data && data.data) {
              if (Number(data.page) === 1) {
                dispatch(setGames(data.data));
              } else {
                dispatch(setMoreGames(data.data));
              }
              dispatch(setLastPage(data.lastPage));
            }
          }
          if (data.statusCode >= 500) {
            setServiceError(true);
          }
        } catch (error: any) {
          if (error.statusCode >= 500) {
            setServiceError(true);
          }
        }
      },
    }),
    gamesSearch: build.query<GamesResponseType, ParamsRequestType>({
      query: (params: ParamsRequestType) => ({
        url: url.config.api.games.games,
        headers: {
          Authorization: getLocalStorageItem(STORAGE_AUTH_TOKEN)
            ? `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`
            : "",
          "f-v": APP_VERSION,
        },
        params,
      }),
      keepUnusedDataFor: 10,
      async onQueryStarted(
        request: ParamsRequestType,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;

          // eslint-disable-next-line sonarjs/no-collapsible-if
          if (data.statusCode === 200) {
            if (data && data.data) {
              if (Number(data.page) === 1) {
                dispatch(setSearchResult(data.data));
              } else {
                dispatch(setSearchResultMore(data.data));
              }
              dispatch(setLastPage(data.lastPage));
            }
          }
          if (data.statusCode >= 500) {
            setServiceError(true);
          }
        } catch (error: any) {
          if (error.statusCode >= 500) {
            setServiceError(true);
          }
        }
      },
    }),
    initByAlias: build.mutation<
      InitGameByAliasResponseType,
      InitByAliasGameRequestType
    >({
      query: (request: InitByAliasGameRequestType) => ({
        url: url.config.api.games.initByAlias(request.alias),
        headers: {
          Authorization: `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`,
          "f-v": APP_VERSION,
        },
        method: "POST",
        body: {
          platform: request.platform,
          mode: request.mode,
          lang: request.lang,
          returnUrl: request.returnUrl,
        },
      }),
      async onQueryStarted(
        request: ParamsRequestType,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;

          // eslint-disable-next-line sonarjs/no-collapsible-if
          if (data.statusCode === 200) {
            if (data && data.url) {
              dispatch(setGameUrl(data.url));
            }
            if (data && data.game) {
              dispatch(setCurrentGame(data.game));
            }
          }
          if (data.statusCode === 404) {
            redirect("/");
          }
          if (data.statusCode >= 500) {
            setServiceError(true);
          }
        } catch (error: any) {
          if (error.statusCode === 404 || error.status === 404) {
            redirect("/");
          }
          if (error.statusCode >= 500) {
            setServiceError(true);
          }
        }
      },
    }),
    initDemoByAlias: build.mutation<
      InitGameByAliasResponseType,
      InitByAliasGameRequestType
    >({
      query: (request: InitByAliasGameRequestType) => ({
        url: url.config.api.games.initDemoByAlias(request.alias),
        method: "POST",
        body: {
          platform: request.platform,
          mode: request.mode,
          lang: request.lang,
          returnUrl: request.returnUrl,
        },
      }),
      async onQueryStarted(
        request: ParamsRequestType,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;

          // eslint-disable-next-line sonarjs/no-collapsible-if
          if (data.statusCode === 200) {
            if (data && data.url) {
              dispatch(setGameUrl(data.url));
            }
            if (data && data.game) {
              dispatch(setCurrentGame(data.game));
            }
          }
          if (data.statusCode === 404) {
            redirect("/");
          }
          if (data.statusCode >= 500) {
            setServiceError(true);
          }
        } catch (error: any) {
          if (error.statusCode === 404 || error.status === 404) {
            redirect("/");
          }
          if (error.statusCode >= 500) {
            setServiceError(true);
          }
        }
      },
    }),
    providers: build.query<ProviderResponseType, string>({
      query: (platform: string) => ({
        url: url.config.api.games.providers,
        headers: {
          Authorization: getLocalStorageItem(STORAGE_AUTH_TOKEN)
            ? `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`
            : "",
          "f-v": APP_VERSION,
        },
        params: {
          platform,
        },
      }),
      async onQueryStarted(request: string, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data.statusCode === 200) {
            dispatch(setProviders(data.data));
          }
          if (data.statusCode >= 500) {
            setServiceError(true);
          }
        } catch (error: any) {
          if (error.statusCode >= 500) {
            setServiceError(true);
          }
        }
      },
    }),
    favorites: build.query<FavoritesResponseType | GameType[], void>({
      query: () => ({
        url: url.config.api.games.favorites,
        headers: {
          Authorization: `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`,
        },
      }),
      keepUnusedDataFor: 1,
    }),
    toggleFavorite: build.mutation<string[], FavoriteRequestType>({
      query: (request: FavoriteRequestType) => ({
        url: url.config.api.games.toggleFavorite(request.urlAlias),
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`,
        },
      }),
    }),
    history: build.query<HistoryResponseType, string>({
      query: () => ({
        url: url.config.api.games.history,
        headers: {
          Authorization: `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`,
        },
      }),
      async onQueryStarted(request: string, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.statusCode === 200) {
            const games = data.data.map((d) => d.game);
            dispatch(setHistory(games));
          }
          if (data.statusCode >= 500) {
            setServiceError(true);
          }
        } catch (error: any) {
          if (error.statusCode >= 500) {
            setServiceError(true);
          }
        }
      },
    }),
    newHistory: build.query<NewHistoryResponseType[], string>({
      query: (platform) => ({
        url: url.config.api.games.history,
        headers: {
          Authorization: `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`,
        },
        params: {
          platform,
        },
      }),
      async onQueryStarted(request: string, { dispatch, queryFulfilled }) {
        try {
          const { data: games } = await queryFulfilled;
          dispatch(setNewHistory(games));
        } catch (error: any) {
          setServiceError(true);
        }
      },
    }),
    freeSpins: build.query({
      query: () => ({
        url: url.config.api.games.freeSpinGames,
        method: "GET",
        headers: {
          Authorization: `Bearer ${getLocalStorageItem(STORAGE_AUTH_TOKEN)}`,
        },
      }),
    }),
  }),
});
